import {
  useRef,
  type ChangeEvent,
  type CSSProperties,
  type DragEvent,
  type ReactNode,
} from "react";

// antd <Upload> / <Upload.Dragger> -> hidden file input (+ drop zone for Dragger)
// with a lightweight file list. Covers the manual-upload pattern: beforeUpload
// (return false / Upload.LIST_IGNORE to skip), customRequest (given the raw file +
// onSuccess/onError/onProgress), and onChange (antd UploadFile shape with
// originFileObj). No MUI equivalent, so this is a Community-gap wrapper.

export interface UploadFile {
  uid?: string;
  name: string;
  status?: "uploading" | "done" | "error" | "removed";
  url?: string;
  originFileObj?: File;
  [k: string]: unknown;
}

export type UploadFileStatus = NonNullable<UploadFile["status"]>;

// Only render http(s)/relative/blob URLs as a clickable download link. `url` is a
// free-form string on a shared, reusable component; a future caller could populate
// it from untrusted input, and target="_blank" + a javascript:/data: URL would be
// an XSS/redirect sink. Anything else falls back to plain text.
// `\/(?!\/)` matches a same-origin relative path ("/x") but NOT a protocol-relative
// URL ("//evil.com"), which the browser would resolve cross-origin (open-redirect).
const isSafeHref = (url: string): boolean => /^(https?:\/\/|\/(?!\/)|blob:)/i.test(url);

// antd/rc-upload's RcFile is a File carrying a generated uid.
export type RcFile = File & { uid: string };

export interface UploadProgressEvent {
  percent: number;
  [k: string]: unknown;
}

export interface CustomRequestOptions {
  file: File;
  filename?: string;
  action?: string;
  method?: string;
  data?: unknown;
  headers?: Record<string, string>;
  onSuccess: (body?: unknown) => void;
  onError: (err?: unknown) => void;
  onProgress: (event: UploadProgressEvent) => void;
}

export interface UploadProps {
  children?: ReactNode;
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  showUploadList?: boolean;
  maxCount?: number;
  listType?: string;
  name?: string;
  fileList?: UploadFile[];
  defaultFileList?: UploadFile[];
  beforeUpload?: (
    file: File,
    fileList: File[]
  ) => boolean | string | void | Promise<boolean | string | void>;
  onChange?: (info: { file: UploadFile; fileList: UploadFile[] }) => void;
  onRemove?: (file: UploadFile) => void;
  customRequest?: (options: CustomRequestOptions) => void;
  style?: CSSProperties;
  className?: string;
  "data-testid"?: string;
  "data-cy"?: string;
}

const noop = () => {};

// antd's uid format (`rc-upload-{timestamp}-{n}`), which callers parse — and
// unique per selection: keying on name+size gave the same file selected twice a
// colliding uid, so the two rows shared one upload's state.
let uidSeq = 0;

const toUploadFile = (file: File): UploadFile => ({
  uid: `rc-upload-${Date.now()}-${++uidSeq}`,
  name: file.name,
  status: "done",
  originFileObj: file,
});

const isThenable = (v: unknown): v is Promise<unknown> =>
  !!v && typeof (v as { then?: unknown }).then === "function";

// Stays synchronous when beforeUpload is absent/sync (so onChange/customRequest
// fire within the same event); switches to a promise chain only if beforeUpload
// returns a thenable.
function processFiles(files: File[], props: UploadProps, existing: UploadFile[]) {
  const { beforeUpload, customRequest, onChange } = props;
  const dispatch = (accepted: File[]) => {
    const next = [...existing];
    accepted.forEach(file => {
      const uf = toUploadFile(file);
      next.push(uf);
      // rc-upload stamps its generated uid onto the File itself, and callers rely
      // on it: an upload done in customRequest has to write its result (e.g. S3
      // bucket/key) back onto the matching row of the caller's own file list, and
      // the uid from onChange is the only handle it has on that row.
      customRequest?.({
        file: Object.assign(file, { uid: uf.uid }),
        filename: uf.name,
        onSuccess: noop,
        onError: noop,
        onProgress: noop,
      });
      onChange?.({ file: uf, fileList: next });
    });
  };
  const kept = (verdict: boolean | string | void) =>
    verdict !== false && verdict !== Upload.LIST_IGNORE;
  if (!beforeUpload) {
    dispatch(files);
    return;
  }
  const verdicts = files.map(file => ({ file, verdict: beforeUpload(file, files) }));
  if (verdicts.some(v => isThenable(v.verdict))) {
    void Promise.all(
      verdicts.map(async v => ({ file: v.file, verdict: await v.verdict }))
    ).then(resolved => dispatch(resolved.filter(r => kept(r.verdict)).map(r => r.file)));
  } else {
    dispatch(verdicts.filter(v => kept(v.verdict as boolean | string | void)).map(v => v.file));
  }
}

const FileListView = ({
  files,
  onRemove,
}: {
  files: UploadFile[];
  onRemove?: (file: UploadFile) => void;
}) => {
  if (!files.length) return null;
  return (
    <ul style={{ listStyle: "none", padding: 0, margin: "8px 0 0" }} className="upload-file-list">
      {files.map(f => (
        <li
          key={f.uid ?? f.name}
          style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, padding: "2px 0" }}
        >
          {f.url && isSafeHref(f.url) ? (
            <a href={f.url} target="_blank" rel="noreferrer">
              {f.name}
            </a>
          ) : (
            <span>{f.name}</span>
          )}
          {onRemove && (
            <button
              type="button"
              aria-label={`remove ${f.name}`}
              onClick={() => onRemove(f)}
              style={{ border: "none", background: "none", cursor: "pointer", color: "#999" }}
            >
              ×
            </button>
          )}
        </li>
      ))}
    </ul>
  );
};

const useUploadInput = (props: UploadProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const existing = props.fileList ?? props.defaultFileList ?? [];
  const handleFiles = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    void processFiles(files, props, existing);
    e.target.value = ""; // allow re-selecting the same file
  };
  return { inputRef, existing, handleFiles };
};

export const Upload = ({ ...props }: UploadProps) => {
  const { children, accept, multiple, disabled, name, onRemove, style, className } = props;
  const { inputRef, existing, handleFiles } = useUploadInput(props);
  return (
    <span style={style} className={className}>
      <span
        style={{ cursor: disabled ? "not-allowed" : "pointer" }}
        onClick={() => !disabled && inputRef.current?.click()}
      >
        {children}
      </span>
      <input
        ref={inputRef}
        type="file"
        name={name}
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        style={{ display: "none" }}
        data-testid={props["data-testid"]}
        data-cy={props["data-cy"]}
        onChange={handleFiles}
      />
      {props.showUploadList !== false && <FileListView files={existing} onRemove={onRemove} />}
    </span>
  );
};

const Dragger = ({ ...props }: UploadProps) => {
  const { children, accept, multiple, disabled, name, onRemove, style, className } = props;
  const { inputRef, existing, handleFiles } = useUploadInput(props);
  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (disabled) return;
    const files = Array.from(e.dataTransfer.files ?? []);
    void processFiles(files, props, existing);
  };
  return (
    <div style={style} className={className}>
      <div
        role="button"
        tabIndex={0}
        onClick={() => !disabled && inputRef.current?.click()}
        onDragOver={e => e.preventDefault()}
        onDrop={onDrop}
        style={{
          border: "1px dashed #d9d9d9",
          borderRadius: 8,
          padding: 16,
          textAlign: "center",
          cursor: disabled ? "not-allowed" : "pointer",
          background: "#fafafa",
        }}
        className="ant-upload ant-upload-drag"
      >
        {children}
      </div>
      <input
        ref={inputRef}
        type="file"
        name={name}
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        style={{ display: "none" }}
        data-testid={props["data-testid"]}
        data-cy={props["data-cy"]}
        onChange={handleFiles}
      />
      {props.showUploadList !== false && <FileListView files={existing} onRemove={onRemove} />}
    </div>
  );
};

Upload.Dragger = Dragger;
// antd sentinel: beforeUpload returning this skips adding the file to the list.
Upload.LIST_IGNORE = "__LIST_IGNORE__" as const;

export default Upload;
